/**
 * Auth API functions.
 *
 * These use a dedicated authFetch (not apiClient) to avoid infinite
 * recursion: apiClient's 401 handler calls refreshToken(), so the
 * refresh call itself must NOT go through apiClient.
 *
 * Public endpoints (login, register, refresh) need no Bearer token.
 * Protected endpoints (logout, me, sessions) attach the Bearer token
 * from the session module.
 */

import { getAccessToken } from "../lib/session";
import type {
  AuthResponse,
  LoginCredentials,
  RegisterCredentials,
  TokenResponse,
  User,
} from "../types";

// Local API_BASE — cannot import from @/shared/lib/api/client due to circular
// dependency (client.ts imports refreshToken from this module).
const API_BASE = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8080";

// Thrown when fetch() itself rejects — DNS failure, connection refused, CORS
// preflight reject, offline. Distinct from non-2xx responses so callers can
// surface "server unreachable" rather than "session expired".
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NetworkError";
  }
}

async function authFetch<T>(
  endpoint: string,
  options?: RequestInit & { authenticated?: boolean }
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (options?.authenticated) {
    const token = getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  if (options?.headers) {
    const extra =
      options.headers instanceof Headers
        ? Array.from(options.headers.entries())
        : Array.isArray(options.headers)
          ? options.headers
          : Object.entries(options.headers);
    for (const [key, value] of extra) {
      headers[key] = value;
    }
  }

  let res: Response;
  try {
    res = await fetch(url, {
      credentials: "include",
      method: options?.method,
      body: options?.body,
      signal: options?.signal,
      headers,
    });
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? err.message : "Network request failed",
    );
  }

  if (!res.ok) {
    const text = await res.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      detail = json.detail || text;
    } catch {
      // raw text
    }
    throw new Error(detail);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export async function login(creds: LoginCredentials): Promise<AuthResponse> {
  return authFetch<AuthResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(creds),
  });
}

export async function register(
  creds: RegisterCredentials
): Promise<AuthResponse> {
  return authFetch<AuthResponse>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify(creds),
  });
}

export async function refreshToken(): Promise<TokenResponse> {
  return authFetch<TokenResponse>("/api/auth/refresh", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function logout(): Promise<void> {
  await authFetch<void>("/api/auth/logout", {
    method: "POST",
    body: JSON.stringify({}),
    authenticated: true,
  });
}

export async function getMe(): Promise<User> {
  return authFetch<User>("/api/auth/me", {
    method: "GET",
    authenticated: true,
  });
}
